import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { getAuthToken } from '../../services/api';
import './ParentSections.css';

const API_BASE_URL = process.env.REACT_APP_API_URL || '';

const ParentSectionProgress = () => {
  const { t } = useTranslation('parent');
  const [selectedChild, setSelectedChild] = useState(null);
  const [selectedReport, setSelectedReport] = useState(null);
  const [children, setChildren] = useState([]);
  const [progressData, setProgressData] = useState(null);
  const [loading, setLoading] = useState(true);

  // Get user from localStorage
  const user = JSON.parse(localStorage.getItem('user') || '{}');
  const parentId = user.id;

  // Fetch children from API
  useEffect(() => {
    const fetchChildren = async () => {
      if (!parentId) {
        setLoading(false);
        return;
      }

      try {
        const token = getAuthToken();
        const response = await fetch(`${API_BASE_URL}/api/parent-dashboard/children/${parentId}`, {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        });

        if (response.ok) {
          const data = await response.json();
          if (data.success) {
            setChildren(data.data);
          }
        }
      } catch (error) {
        console.error('Error fetching children:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchChildren();
  }, [parentId]);

  // Fetch progress when child is selected
  useEffect(() => {
    const fetchProgress = async () => {
      if (!selectedChild) {
        setProgressData(null);
        return;
      }

      try {
        const token = getAuthToken();
        const response = await fetch(`${API_BASE_URL}/api/parent-dashboard/children/${selectedChild}/progress`, {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        });

        if (response.ok) {
          const data = await response.json();
          if (data.success) {
            setProgressData(data.data);
          }
        }
      } catch (error) {
        console.error('Error fetching progress:', error);
      }
    };

    fetchProgress();
  }, [selectedChild]);

  const selectedData = progressData;

  return (
    <div className="parent-main-content">
      <div className="parent-content-header">
        <h1 className="parent-content-title">{t('progress.title')}</h1>
        <p className="parent-content-subtitle">{t('progress.subtitle')}</p>
      </div>

      {/* Child Selector */}
      <div className="parent-section">
        <div className="parent-section-header">
          <h2 className="parent-section-title">{t('progress.selectChild')}</h2>
        </div>
        <div className="parent-child-selector">
          {children.map((child) => (
            <button
              key={child.id}
              className={`parent-child-select-btn ${selectedChild === child.id ? 'active' : ''}`}
              onClick={() => setSelectedChild(child.id)}
            >
              <span>{child.name}</span>
              <span className="parent-child-grade">{child.grade}</span>
            </button>
          ))}
        </div>
      </div>

      {selectedData && (
        <>
          {/* Overall Progress */}
          <div className="parent-section">
            <div className="parent-section-header">
              <h2 className="parent-section-title">{t('progress.overallProgress')}</h2>
            </div>
            <div className="parent-progress-overview">
              <div className="parent-progress-card">
                <div className="parent-progress-circle">
                  <div className="parent-progress-circle-inner">
                    <span className="parent-progress-percentage">{selectedData.overallProgress}%</span>
                  </div>
                </div>
                <h3>{t('progress.completionRate')}</h3>
              </div>

              <div className="parent-progress-stats">
                <div className="parent-progress-stat">
                  <i className="material-icons">school</i>
                  <div>
                    <strong>{selectedData.completedCourses} / {selectedData.enrolledCourses}</strong>
                    <p>{t('progress.courses')}</p>
                  </div>
                </div>
                <div className="parent-progress-stat">
                  <i className="material-icons">translate</i>
                  <div>
                    <strong>{selectedData.vocabularyWords}</strong>
                    <p>{t('progress.vocabularyWords')}</p>
                  </div>
                </div>
                <div className="parent-progress-stat">
                  <i className="material-icons">fitness_center</i>
                  <div>
                    <strong>{selectedData.practiceSessions}</strong>
                    <p>{t('progress.practiceSessions')}</p>
                  </div>
                </div>
                <div className="parent-progress-stat">
                  <i className="material-icons">schedule</i>
                  <div>
                    <strong>{new Date(selectedData.lastActivity).toLocaleDateString()}</strong>
                    <p>{t('progress.lastActivity')}</p>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Course Progress */}
          <div className="parent-section">
            <div className="parent-section-header">
              <h2 className="parent-section-title">{t('progress.courseProgress')}</h2>
            </div>
            <div className="parent-courses-list">
              {selectedData.courses.map((course) => (
                <div key={course.id} className="parent-course-progress-card">
                  <div className="parent-course-header">
                    <h3 className="parent-course-name">{course.name}</h3>
                    <span className={`parent-course-status ${course.status}`}>
                      {course.status === 'completed' && t('progress.completed')}
                      {course.status === 'in-progress' && t('progress.inProgress')}
                      {course.status === 'not-started' && t('progress.notStarted')}
                    </span>
                  </div>
                  <div className="parent-course-progress">
                    <div className="parent-progress-bar">
                      <div 
                        className="parent-progress-fill" 
                        style={{ width: `${course.progress}%` }}
                      ></div>
                    </div>
                    <span className="parent-progress-text">{course.progress}%</span>
                  </div>
                  {course.completedDate && (
                    <p className="parent-course-date">
                      {t('progress.completedOn')} {new Date(course.completedDate).toLocaleDateString()}
                    </p>
                  )}
                  {course.lastAccessed && (
                    <p className="parent-course-date">
                      {t('progress.lastAccessed')} {new Date(course.lastAccessed).toLocaleDateString()}
                    </p>
                  )}
                </div>
              ))}
            </div>
          </div>

          {/* Strengths and Improvements */}
          <div className="parent-section">
            <div className="parent-section-header">
              <h2 className="parent-section-title">{t('progress.performanceAnalysis')}</h2>
            </div>
            <div className="parent-analysis-grid">
              <div className="parent-analysis-card success">
                <h3>{t('progress.strengths')}</h3>
                <ul className="parent-analysis-list">
                  {selectedData.strengths.map((strength, index) => (
                    <li key={index}>
                      <i className="material-icons">check_circle</i>
                      {strength}
                    </li>
                  ))}
                </ul>
              </div>
              <div className="parent-analysis-card warning">
                <h3>{t('progress.areasForImprovement')}</h3>
                <ul className="parent-analysis-list">
                  {selectedData.improvements.map((improvement, index) => (
                    <li key={index}>
                      <i className="material-icons">trending_up</i>
                      {improvement}
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          </div>

          {/* Generate Report */}
          <div className="parent-section">
            <div className="parent-section-header">
              <h2 className="parent-section-title">{t('progress.reports')}</h2>
            </div>
            <div className="parent-reports-actions">
              <button className="parent-btn primary">
                <i className="material-icons">description</i>
                {t('progress.generateReport')}
              </button>
              <button className="parent-btn secondary">
                <i className="material-icons">download</i>
                {t('progress.downloadReport')}
              </button>
            </div>
          </div>
        </>
      )}

      {!selectedChild && (
        <div className="parent-empty-state">
          <i className="material-icons">person_outline</i>
          <p>{t('progress.selectChildFirst')}</p>
        </div>
      )}
    </div>
  );
};

export default ParentSectionProgress;

